import { lazy, Suspense, useState } from 'react';
import {
  createEnrolmentSession,
  getEnrolmentState,
  getMe,
  submitEnrolmentResult,
  type CreatedSession,
} from '../api/endpoints.js';
import type { ApiFailure } from '../api/client.js';
import { useAction, useApi, useIntentKey } from '../api/hooks.js';
import {
  Async,
  Badge,
  Button,
  Failure,
  Field,
  KeyValue,
  Loading,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

/**
 * Amplify UI and TensorFlow ride along with the detector and are needed by this
 * screen alone. Splitting them out keeps them off every other screen's first
 * paint; the chunk is fetched when a capture actually starts.
 */
const LivenessCapture = lazy(() => import('./liveness-capture.js'));

/** Terminal states stop the poll; anything else keeps it running. */
const TERMINAL = new Set(['enrolled', 'failed', 'none', 'not_enrolled']);

/**
 * `spent` covers both a failure and an abandonment, because the consequence is
 * identical and it is the one thing a person needs told: creating the session
 * burned a provider attempt, so there is nothing here to retry — only a new
 * session to create.
 */
type CaptureStage = 'idle' | 'capturing' | 'analysed' | 'spent';

export function EnrolmentScreen() {
  const me = useApi(getMe, []);
  const [subjectId, setSubjectId] = useState('');
  const [session, setSession] = useState<CreatedSession | null>(null);
  const [stage, setStage] = useState<CaptureStage>('idle');
  const [captureError, setCaptureError] = useState<ApiFailure | null>(null);
  const resultKey = useIntentKey();

  const state = useApi(() => getEnrolmentState(subjectId), [subjectId], {
    skip: subjectId.length === 0,
    pollMs: 5000,
  });

  const submit = useAction(submitEnrolmentResult, () => {
    resultKey.next();
    state.reload();
  });

  const create = useAction(createEnrolmentSession, (created) => {
    setSession(created);
    setCaptureError(null);
    setStage('capturing');
  });

  const status = state.data?.status;
  const settled = status !== undefined && TERMINAL.has(status);

  return (
    <Screen
      title="Enrolment"
      lede="LIVENESS_GATEWAY=live on dev, so these calls reach the real services deployment. No route here carries image bytes — the video streams from this browser straight to Rekognition, and the reference frame goes from services to S3 through a presigned PUT."
    >
      <Async state={me}>
        {(data) => (
          <Panel title="subject">
            <Row>
              <Field label="subject_person_id" value={subjectId} onChange={setSubjectId} />
              <Button variant="secondary" onClick={() => setSubjectId(data.person.person_id)}>
                Use my person_id
              </Button>
            </Row>
            <p className="muted" style={{ marginTop: 8 }}>
              consent_signed: <Badge>{String(data.onboarding.consent_signed)}</Badge> · enrolled:{' '}
              <Badge>{String(data.onboarding.enrolled)}</Badge>
            </p>
          </Panel>
        )}
      </Async>

      {subjectId.length > 0 && (
        <Panel title="enrolment state">
          <Async state={state}>
            {(data) => (
              <>
                <KeyValue
                  rows={[
                    ['status', <Badge key="s">{data.status}</Badge>],
                    ['enrolled_at', data.enrolled_at],
                  ]}
                />
                <p className="muted">
                  {settled ? 'terminal — polling stopped' : 'polling every 5s'}
                </p>
                <Raw value={data} />
              </>
            )}
          </Async>
        </Panel>
      )}

      <Panel title="1 · create a liveness session">
        <Row>
          <Button
            onClick={() => create.run(subjectId)}
            disabled={create.pending || subjectId.length === 0}
          >
            {create.pending ? 'creating…' : session === null ? 'Create session' : 'Create a new session'}
          </Button>
        </Row>
        <p className="muted" style={{ marginTop: 8 }}>
          No Idempotency-Key is sent, and the route 400s if one is present: creating a session burns
          a liveness attempt, so a retry must create a new one.
        </p>
        {create.error !== null && (
          <Failure
            error={create.error}
            hint={
              {
                CONSENT_NOT_SIGNED: 'Sign consent for this subject first.',
                UNDER_MIN_AGE: 'Below MIN_ENROLMENT_AGE — the enrolment floor, not the discovery floor.',
                DOB_REQUIRED:
                  'This subject has no date of birth recorded. It is write-once and decides self- versus guardian-signature on a legal record, so it is set on the profile screen, never here.',
                SESSION_IN_PROGRESS: 'A session is already open for this subject.',
                LIVENESS_NOT_READY: 'Services are not ready to take a session.',
                TOO_MANY_ATTEMPTS: 'LIVENESS_MAX_ATTEMPTS_24H is exhausted for this subject.',
              }[create.error.code]
            }
          />
        )}
        {session !== null && (
          <>
            <KeyValue
              rows={[
                ['session_id', <code key="s">{session.session_id}</code>],
                ['provider_session_id', <code key="p">{session.provider_session_id}</code>],
                ['region', session.region],
                ['expires_at', session.expires_at],
              ]}
            />
            <Raw value={session} />
          </>
        )}
      </Panel>

      <Panel title="2 · capture">
        {session === null ? (
          <p className="muted">Create a session first — the detector streams against it.</p>
        ) : stage === 'capturing' ? (
          <Suspense fallback={<Loading />}>
            <LivenessCapture
              session={session}
              onAnalysisComplete={() => {
                setStage('analysed');
                // The detector knows only that analysis finished. The verdict is
                // read server-side, so the result route is the next call and
                // there is no reason to make a person press a button for it.
                submit.run(session.session_id, resultKey.key);
              }}
              onFailure={(failure) => {
                setCaptureError(failure);
                setStage('spent');
              }}
              onCancel={() => {
                setCaptureError(null);
                setStage('spent');
              }}
            />
          </Suspense>
        ) : stage === 'analysed' ? (
          <p className="muted">
            Capture finished and the result was submitted below. Whether it passed is not known
            here — the detector never sees the verdict, and this client is not told one it did not
            receive.
          </p>
        ) : stage === 'spent' ? (
          <>
            {captureError === null ? (
              <Note>
                The capture was abandoned before it completed. <strong>Nothing was enrolled.</strong>{' '}
                This session is spent — creating it burned a provider attempt — so step 1 above is
                the way back, not a retry.
              </Note>
            ) : (
              <Failure
                error={captureError}
                hint="The capture did not complete, so nothing was enrolled. This session is spent: create a new one in step 1 rather than retrying."
              />
            )}
          </>
        ) : (
          <p className="muted">Create a session to start the capture.</p>
        )}
      </Panel>

      <Panel title="3 · submit the result">
        <p className="muted">
          An empty body by design: everything services need is computed server-side — most
          importantly <code>subject_is_adult</code>, which a client must never get to choose. Step 2
          fires this automatically once analysis finishes; the button exercises the route on its own.
        </p>
        <Row>
          <Button
            onClick={() => {
              if (session !== null) submit.run(session.session_id, resultKey.key);
            }}
            disabled={submit.pending || session === null}
          >
            {submit.pending ? 'submitting…' : 'Submit result'}
          </Button>
        </Row>
        <p className="muted" style={{ marginTop: 8 }}>
          Idempotency-Key: <code>{resultKey.key}</code> — held across retries, which is what services’
          own same-key replay expects.
        </p>
        {submit.error !== null && (
          <Failure
            error={submit.error}
            hint={
              {
                LIVENESS_FAILED:
                  'The provider judged the capture not live. Every failed check arrives as this code today — services store a failure_reason and do not return it, so a no-face failure is indistinguishable from a not-live one here.',
                NO_FACE_DETECTED:
                  'No face in the reference frame. Nothing produces this code yet (src/errors/codes.ts:72) — seeing it means services began returning failure_reason.',
                QUALITY_TOO_LOW: 'Below the quality floor — that session is spent, start a fresh one.',
                IDENTITY_CONFLICT:
                  'This face is already enrolled to another person. Not retryable, and deliberately carries nothing about who: identical twins are the real case, and a human has to resolve it.',
                SESSION_CONSUMED: 'This session was already submitted — create a new one.',
                SESSION_EXPIRED: 'The session TTL passed (LIVENESS_SESSION_TTL).',
              }[submit.error.code]
            }
          />
        )}
        {submit.result !== null && <Raw label="result response" value={submit.result} />}
      </Panel>
    </Screen>
  );
}
